import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatPrice } from '../../../utils/formatters';

const MobilePartsDrawer = ({
  isOpen,
  onClose,
  components,
  searchTerm,
  onSearchChange,
  onComponentSelect,
  selectedComponent,
  viewMode = 'shaded',
  onToggleZRay,
  zRayComponentIds = [],
  componentVisibility = {},
  onToggleVisibility,
  onIsolate,
  onAddComponentToCart
}) => {
  const filteredComponents = useMemo(() => components?.filter(component =>
    component?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    component?.group?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  ), [components, searchTerm]);

  // Lock body scroll and prevent keyboard focus shifts from scrolling the main frame!
  React.useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalWidth = document.body.style.width;
      const originalHeight = document.body.style.height;

      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';

      window.scrollTo(0, 0);
      document.body.scrollTop = 0;

      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.width = originalWidth;
        document.body.style.height = originalHeight;
      };
    }
  }, [isOpen]);

  const handleInputFocus = () => {
    // Reset any native browser-keyboard scroll shifts
    setTimeout(() => {
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
    }, 50);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Symmetrical Semi-Transparent Backdrop Scrim - Tapping closes search instantly! */}
      <div 
        className="fixed inset-0 bg-black/45 backdrop-blur-xs z-[999] transition-opacity animate-in fade-in duration-300" 
        onClick={onClose} 
      />

      {/* Symmetrical Sliding Bottom Sheet Drawer - Preserves visual context of the car model! */}
      <div className="fixed bottom-0 left-0 right-0 z-[1000] h-[65vh] max-h-[65vh] bg-background rounded-t-3xl border-t border-border shadow-floating flex flex-col overflow-hidden animate-in slide-in-from-bottom duration-300 pb-[env(safe-area-inset-bottom,0px)]">
        {/* Beautiful Apple/Google Drag Handle Pill Indicator */}
        <div className="w-12 h-1.5 bg-muted-foreground/30 rounded-full mx-auto my-2.5 shrink-0" onClick={onClose} />

        {/* Header */}
        <div className="flex items-center justify-between px-4 pb-3 border-b border-border bg-muted/10">
          <h3 className="font-extrabold text-base">Parts List & Search</h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="shrink-0 h-8 w-8 rounded-full bg-muted/40">
            <Icon name="X" size={18} />
          </Button>
        </div>

      {/* Search in Drawer */}
      <div className="p-4 border-b border-border">
        <div className="relative">
          <input
            type="text"
            className="w-full bg-muted border-none rounded-lg py-3 px-10 text-base focus:ring-2 focus:ring-primary"
            placeholder="Search parts..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            onFocus={handleInputFocus}
            autoFocus
          />
          <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          {searchTerm && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            >
              <Icon name="X" size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto">
        {filteredComponents?.length > 0 ? (
          <div className="divide-y divide-border">
            {filteredComponents.map((component) => (
              <div key={component.id} className="p-4 flex flex-col gap-2">
                <div 
                  className={`flex items-center justify-between gap-4 py-1 ${selectedComponent?.id === component.id ? 'bg-muted/30 rounded-lg px-2' : ''}`}
                >
                  {/* Symmetrical Mobile Visibility & Isolate Toggles */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onIsolate(component.id);
                      }}
                      className="p-2 hover:bg-accent/10 active:bg-accent/20 rounded-lg transition"
                      title="Isolate component"
                    >
                      <Icon name="Focus" size={16} className="text-accent" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleVisibility(component.id);
                      }}
                      className="p-2 hover:bg-muted active:bg-muted-foreground/10 rounded-lg transition"
                      title="Toggle visibility"
                    >
                      <Icon 
                        name={(componentVisibility[component.id] ?? true) ? "Eye" : "EyeOff"} 
                        size={16} 
                        className={(componentVisibility[component.id] ?? true) ? 'text-success' : 'text-muted-foreground'}
                      />
                    </button>
                    {/* Z-Ray Toggle Button */}
                    {(() => {
                      const isZRay = zRayComponentIds && (
                        zRayComponentIds.includes(component.id) ||
                        (component.name && zRayComponentIds.includes(component.name)) ||
                        (component.dbComponentId && zRayComponentIds.includes(component.dbComponentId))
                      );
                      return (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onToggleZRay) onToggleZRay(component);
                          }}
                          className="p-2 hover:bg-cyan-50 active:bg-cyan-100 rounded-lg transition"
                          title={isZRay ? "Turn off Z-Ray" : "Render this component in Z-Ray"}
                        >
                          <Icon 
                            name="Scan" 
                            size={16} 
                            className={isZRay ? "text-cyan-500 font-bold" : "text-muted-foreground"} 
                          />
                        </button>
                      );
                    })()}
                  </div>

                  {/* Component Select Click Target */}
                  <div 
                    className="flex-1 min-w-0 flex items-center justify-between gap-2 cursor-pointer active:opacity-70"
                    onClick={() => {
                      onComponentSelect(component);
                      onClose();
                    }}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`text-sm truncate ${selectedComponent?.id === component.id ? 'text-primary font-bold' : ''}`}>
                        {component.name}
                      </span>
                      {(component.is3dManufacturing || component.isCncCapable) && (
                        <span className="px-1.5 py-0.5 bg-orange-600 text-white text-[8px] font-black rounded shadow-sm shrink-0">3D</span>
                      )}
                    </div>
                    <Icon name="ChevronRight" size={16} className="text-muted-foreground shrink-0" />
                  </div>
                </div>
                
                {/* Seller Options */}
                {(() => {
                  const options = (component.availableOptions || []).filter(o => o.price > 0 || o.is3dManufacturing || o.isCncCapable);
                  if (options.length === 0 && (component.price || component.is3dManufacturing || component.isCncCapable)) options.push(component);
                  
                  if (options.length > 0) {
                    return (
                      <div className="flex flex-col gap-2 pl-4">
                        {options.map(option => (
                          <div 
                            key={option.id || option.dbComponentId}
                            className="flex items-center justify-between bg-muted/30 p-3 rounded-lg"
                          >
                            <div className="flex flex-col">
                              <span className="text-xs font-medium">{option.sellerBusinessName || 'Direct Seller'}</span>
                              <span className="text-sm font-bold text-primary">{formatPrice(option.price)}</span>
                            </div>
                            <Button 
                              size="sm" 
                              onClick={(e) => {
                                e.stopPropagation();
                                onAddComponentToCart(option);
                              }}
                            >
                              Add to Cart
                            </Button>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-muted-foreground">
            No parts found matching "{searchTerm}"
          </div>
        )}
      </div>
    </div>
  </>
  );
};

export default MobilePartsDrawer;
